import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import { insertMention, mentionCandidates, mentionQuery, mentionsIn, type Mention } from "../mentions";

/** How tall the input may grow before it scrolls instead — a few lines, so a long message can't eat the thread. */
const MAX_INPUT_PX = 160;

/** The message input row: hidden attachment picker + attach button, text field (Enter sends, Shift+Enter
 *  starts a new line, paste images), Send. The field is a textarea so a message can carry line breaks at all,
 *  and it grows with its content rather than scrolling a one-line box. */
export function Composer({
  fileRef,
  draft,
  setDraft,
  onSend,
  onFiles,
  placeholder = "Message",
  mentionables,
}: {
  /** Attach is optional: omit fileRef+onFiles for a text-only composer (e.g. group chat) — the 📎 hides. */
  fileRef?: RefObject<HTMLInputElement>;
  draft: string;
  setDraft: (v: string) => void;
  /** Sends the draft, with the sessions it @mentions when the composer offers any. */
  onSend: (mentions?: Mention[]) => void;
  onFiles?: (files: File[]) => void;
  placeholder?: string;
  /** The sessions an `@` offers; none offered when omitted. */
  mentionables?: Mention[];
}) {
  const attach = !!(fileRef && onFiles);
  const box = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState<Mention[]>([]);
  const [active, setActive] = useState(0);
  const [closedAt, setClosedAt] = useState<number | null>(null);
  const query = mentionables?.length ? mentionQuery(draft, caret) : null;
  const options = query && query.start !== closedAt ? mentionCandidates(mentionables!, query.query, Infinity) : [];
  const pick = (m: Mention) => {
    if (!query) return;
    const r = insertMention(draft, query.start, caret, m);
    setDraft(r.text);
    setPicked((p) => [...p, m]);
    setCaret(r.caret);
    setActive(0);
    requestAnimationFrame(() => box.current?.setSelectionRange(r.caret, r.caret));
  };
  const send = () => {
    const mentions = mentionsIn(draft, picked);
    setPicked([]);
    onSend(mentions.length ? mentions : undefined);
  };
  // Re-measure on every draft change, including the reset to "" after sending — otherwise the box keeps the
  // height of the message that was just sent.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto"; // shrink first, so scrollHeight reports the content's height and not the old one
    const wanted = el.scrollHeight;
    el.style.height = `${Math.min(wanted, MAX_INPUT_PX)}px`;
    // Only let it scroll once it has actually hit the ceiling. Left on `auto`, sub-pixel rounding of the
    // content height against the box height is enough to draw a scrollbar in an empty one-line field.
    el.style.overflowY = wanted > MAX_INPUT_PX ? "auto" : "hidden";
  }, [draft]);
  return (
    <div className="composer">
      {attach && (
        <input
          ref={fileRef}
          type="file"
          style={{ display: "none" }}
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            onFiles!(files);
          }}
        />
      )}
      {attach && (
        <button className="attach" onClick={() => fileRef!.current?.click()} title="Attach a picture or a file">
          📎
        </button>
      )}
      <textarea
        ref={box}
        rows={1}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setActive(0);
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyDown={(e) => {
          if (options.length > 0) {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => (a + (e.key === "ArrowDown" ? 1 : options.length - 1)) % options.length);
              return;
            }
            if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
              e.preventDefault();
              pick(options[Math.min(active, options.length - 1)]!);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setClosedAt(query!.start);
              return;
            }
          }
          if (e.key !== "Enter") return;
          // Shift+Enter is the new line, so it must reach the textarea untouched. An Enter that is closing an
          // IME candidate is not a send either — it belongs to the word being composed.
          if (e.shiftKey || e.nativeEvent.isComposing) return;
          e.preventDefault(); // else the send is followed by a stray newline in the cleared box
          send();
        }}
        onPaste={(e) => {
          if (!onFiles) return;
          const files = Array.from(e.clipboardData.items)
            .filter((it) => it.type.startsWith("image/"))
            .map((it) => it.getAsFile())
            .filter((f): f is File => !!f);
          if (files.length) {
            e.preventDefault();
            onFiles(files);
          }
        }}
        placeholder={placeholder}
      />
      <button onClick={send}>Send</button>
      {options.length > 0 && (
        <ul className="mention-list" role="listbox">
          {options.map((m, i) => (
            <li
              key={m.session}
              role="option"
              aria-selected={i === active}
              className={i === active ? "active" : undefined}
              ref={i === active ? (el) => el?.scrollIntoView?.({ block: "nearest" }) : undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(m);
              }}
            >
              @{m.name}
            </li>
          ))}
        </ul>
      )}

    </div>
  );
}
